import DateUtils = ToroUtils.ToroDate;
import SaZoneType = ToroSaEnums.SaZoneType;
import TableDataType = ToroSaEnums.TableDataType;

import { ToroSaEnums } from '../enums/spatial-adjust.enums';
import { ToroUtils } from '../../common/utils/_toro.utils';

export class SpatialAdjustUtil {

    // True if the station has not been pushed during the current calendar day (local to the course).
    static isStationPushable(utcDate: Date | string, utcOffsetInSeconds: number): boolean {
        const dateObj = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
        return DateUtils.isUtcDateAtLeastOneDayAgoLocally(dateObj, utcOffsetInSeconds);
    }

    // True if the scan date is from today (local to the course).
    static isScanCurrent(localScanDateString: string, utcOffsetInSeconds: number): boolean {
        return DateUtils.isDateTodayLocally(localScanDateString, utcOffsetInSeconds);
    }

    // Compares only primitive-valued properties.
    // Arrays, dates, and nested objects are ignored.
    static shallowEqual(obj1: any, obj2: any): boolean {
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) return false;

        return keys1.every(key => {
            const value1 = obj1[key];
            const value2 = obj2[key];

            const isObject1 = value1 !== null && typeof value1 === "object";
            const isObject2 = value2 !== null && typeof value2 === "object";

            if (isObject1 || isObject2) return true;

            return value1 === value2;
        });
    }

    static deepEqual(value1: any, value2: any): boolean {
        // Handles equal primitives, identical references, and null.
        if (Object.is(value1, value2)) return true;

        if (
            value1 === null ||
            value2 === null ||
            typeof value1 !== "object" ||
            typeof value2 !== "object"
        ) {
            return false;
        }

        if (value1 instanceof Date || value2 instanceof Date) {
            return (
                value1 instanceof Date &&
                value2 instanceof Date &&
                value1.getTime() === value2.getTime()
            );
        }

        if (Array.isArray(value1) || Array.isArray(value2)) {
            if (!Array.isArray(value1) || !Array.isArray(value2)) {
                return false;
            }

            if (value1.length !== value2.length) return false;

            return value1.every((item, index) =>
                this.deepEqual(item, value2[index])
            );
        }

        const keys1 = Object.keys(value1);
        const keys2 = Object.keys(value2);

        if (keys1.length !== keys2.length) return false;

        return keys1.every(
            key =>
                Object.prototype.hasOwnProperty.call(value2, key) &&
                this.deepEqual(value1[key], value2[key])
        );
    }

    static getTableDataTypeFromSaZoneType(saZoneType: SaZoneType): TableDataType {
        switch (saZoneType) {
            case SaZoneType.Area:
                return TableDataType.Area;
            case SaZoneType.Hole:
                return TableDataType.Hole;
            case SaZoneType.Station:
                return TableDataType.Station;
            default:
                throw new Error(`Unsupported SaZoneType: ${saZoneType}`);
        }
    }

    static getSaZoneTypeFromTableDataType(tableDataType: TableDataType): SaZoneType {
        switch (tableDataType) {
            case TableDataType.Area:
                return SaZoneType.Area;
            case TableDataType.Hole:
                return SaZoneType.Hole;
            case TableDataType.Station:
                return SaZoneType.Station;
            default:
                throw new Error(`Unsupported TableDataType: ${tableDataType}`);
        }
    }

    static convertMmToInches(mm: number, decimals?: number): number {
        const result = mm / 25.4;
        return typeof decimals === 'number' ? Number(result.toFixed(decimals)) : result;
    }

    static convertInchesToMm(inches: number, decimals?: number): number {
        const result = inches * 25.4;
        return typeof decimals === 'number' ? Number(result.toFixed(decimals)) : result;
    }
}
